import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { type OperatorRole } from "@oathera/contract/rbac";
import { ApiError } from "../lib/api.ts";

/**
 * The auth state the login / account / admin screens read (controlplane-auth task 11.2, design
 * §Reference alignment "Auth composition"). It echoes AgentDNA's `src/context/AuthContext.tsx` as a
 * composition — a provider holding the current user/team/role plus `signOut` / `switchTeam` — but
 * NOT its mechanism.
 *
 * DELIBERATE DIVERGENCE (design §Deliberate divergences 2, flagged for owner sign-off): AgentDNA
 * stores a JWT in `localStorage` and decodes the identity claims client-side. **We do NOT.** The
 * control plane's trust boundary is the server-side BFF session (Requirement 2 / Properties 2, 3):
 *
 *   - The browser holds only the opaque `oa_session` cookie (`HttpOnly; Secure; SameSite=Lax`); the
 *     client cannot read it and never sees or decodes identity claims.
 *   - This context therefore DERIVES its auth state from a SERVER RESPONSE, not from any token in
 *     `localStorage`. It issues a single same-origin GET for the current session (cookie sent
 *     automatically) and reads `{ user, team, role, teams }` from the body. A `401` is the
 *     server telling us there is no session — surfaced here as `authenticated: false`, which is the
 *     SPA's client-side cue to show the login route (design §Error Handling: the API answers `/api/*`
 *     with 401 JSON, never a redirect; the SPA reacts).
 *   - `signOut` and `switchTeam` are server round-trips (`POST /auth/logout`, `POST
 *     /auth/switch-team`); after each we invalidate the session query so the state re-derives from
 *     the server, which remains the sole authority. We never mutate an in-memory claim set as if it
 *     were the truth.
 *
 * Data-fetching reuses `@tanstack/react-query` + the shared `apiFetch` wrapper, matching the SPA's
 * existing pattern (`lib/query-client.ts`, `lib/api.ts`). The query key is stable so any screen can
 * read the same cached session.
 */

/** The current operator, as returned by the server session endpoint. Never built from a token. */
export interface AuthUser {
  /** The opaque envelope `sub` (never an email; design §Data Models). */
  readonly id: string;
  /** The operator's email, for display only. */
  readonly email: string;
  /** An optional friendly name. */
  readonly displayName?: string;
}

/** A team (tenant) the operator belongs to, with the role the server resolved for it. */
export interface AuthTeam {
  /** The tenant id. */
  readonly id: string;
  /** A human label for the team, when the server provides one; defaults to the id. */
  readonly name?: string;
  /** The operator's role within this team (viewer < operator < admin). */
  readonly role: OperatorRole;
}

/**
 * The resolved, authenticated identity. All of it comes from the server session response — this is
 * the whole point of the divergence note above.
 */
export interface AuthIdentity {
  readonly user: AuthUser;
  /** The currently selected team, or `null` when the operator has memberships but none is selected. */
  readonly team: AuthTeam | null;
  /** The role for the selected team, hoisted for convenience; `null` when no team is selected. */
  readonly role: OperatorRole | null;
  /** Every team the operator belongs to (drives the team switcher). */
  readonly teams: readonly AuthTeam[];
  /** True when the operator is a superadmin (an admin of the root team): may create/see all teams. */
  readonly isSuperadmin: boolean;
}

/** The context value the screens consume. */
export interface AuthState {
  /** True once the server has confirmed a valid session. */
  readonly authenticated: boolean;
  /**
   * True when the session is a PENDING-ONLY session (per-team-signin-factors R9.2/R9.3, task 14): an
   * authenticated user whose only non-banned membership(s) are `pending_approval`, so the server
   * granted a session but resolved NO active team. The server mints a session only for a user with
   * ≥1 active team or ≥1 non-banned pending membership (worker task 6), and `GET /auth/session`
   * returns `teams: []` with `selected_tenant: null` for the pending-only case (its membership list
   * is already filtered to ACTIVE, NON-BANNED rows, worker task 8). So on the client an authenticated
   * session with no teams IS a pending-only session — no extra server flag is needed. This drives the
   * SPA route guard (App.tsx) that locks the session onto the Access Requested screen.
   */
  readonly pendingApproval: boolean;
  /** The resolved identity, or `null` while loading or when unauthenticated. */
  readonly identity: AuthIdentity | null;
  /** True on the first load before the session query has settled. */
  readonly loading: boolean;
  /**
   * A non-auth error (network, server, malformed) from the session read. A `401` is NOT an error —
   * it is the normal "no session" answer and sets `authenticated: false` instead.
   */
  readonly error: ApiError | null;
  /** Re-read the session from the server (e.g. after a login elsewhere in the shell). */
  readonly refresh: () => Promise<void>;
  /** End the session server-side (`POST /auth/logout`), then re-derive state (now unauthenticated). */
  readonly signOut: () => Promise<void>;
  /** True while a `signOut` round-trip is in flight. */
  readonly signingOut: boolean;
  /**
   * Switch the selected team within the same session (`POST /auth/switch-team`). The server rotates
   * the session and re-resolves `(tid, role)`; we then invalidate the session query so the new team
   * drives the UI. Resolves when the switch has been applied; rejects (ApiError) on refusal.
   */
  readonly switchTeam: (tenantId: string) => Promise<void>;
  /** True while a `switchTeam` round-trip is in flight. */
  readonly switchingTeam: boolean;
}

/** The query key for the current session read; shared so every screen hits the same cache entry. */
export const AUTH_SESSION_KEY = ["auth", "session"] as const;

/** The server endpoints this context talks to (same origin; the cookie is sent automatically). */
const SESSION_PATH = "/auth/session";
const LOGOUT_PATH = "/auth/logout";
const SWITCH_TEAM_PATH = "/auth/switch-team";

/**
 * The management/auth response envelope `{ status, message, data }` (admin-server `AgentResponse`;
 * design §admin-server data/API conventions). We read the session identity out of `data`.
 */
interface AuthEnvelope<T> {
  readonly status: boolean;
  readonly message: string;
  readonly data: T | null;
}

/** The `data` shape of the session read. Fields are validated defensively before use. */
interface SessionData {
  readonly user?: {
    readonly id?: unknown;
    readonly email?: unknown;
    readonly display_name?: unknown;
  };
  readonly selected_tenant?: unknown;
  readonly is_superadmin?: unknown;
  readonly teams?: readonly {
    readonly id?: unknown;
    readonly name?: unknown;
    readonly role?: unknown;
  }[];
}

const KNOWN_ROLES: readonly OperatorRole[] = ["viewer", "operator", "admin"];

function asRole(value: unknown): OperatorRole | null {
  return typeof value === "string" && (KNOWN_ROLES as readonly string[]).includes(value)
    ? (value as OperatorRole)
    : null;
}

/**
 * Shape the raw server body into an {@link AuthIdentity}, or `null` when the body does not describe a
 * usable session. Pure and defensive: the server is trusted, but a malformed body must not crash the
 * shell — it degrades to "unauthenticated" (fail closed).
 */
export function parseSessionData(data: SessionData | null | undefined): AuthIdentity | null {
  if (data === null || data === undefined) return null;
  const rawUser = data.user;
  if (rawUser === undefined || typeof rawUser.id !== "string" || rawUser.id.length === 0) {
    return null;
  }
  const user: AuthUser = {
    id: rawUser.id,
    email: typeof rawUser.email === "string" ? rawUser.email : "",
    ...(typeof rawUser.display_name === "string" ? { displayName: rawUser.display_name } : {}),
  };

  const teams: AuthTeam[] = [];
  for (const raw of data.teams ?? []) {
    if (typeof raw.id !== "string" || raw.id.length === 0) continue;
    const role = asRole(raw.role);
    if (role === null) continue;
    teams.push({
      id: raw.id,
      role,
      ...(typeof raw.name === "string" ? { name: raw.name } : {}),
    });
  }

  const selected = typeof data.selected_tenant === "string" ? data.selected_tenant : null;
  const team = selected === null ? null : (teams.find((t) => t.id === selected) ?? null);

  const isSuperadmin = data.is_superadmin === true;
  return { user, team, role: team?.role ?? null, teams, isSuperadmin };
}

/**
 * Whether a resolved identity is a PENDING-ONLY session (per-team-signin-factors R9.2/R9.3, task 14).
 * Pure so the SPA route guard and its tests share one definition. The server's session membership
 * list is already ACTIVE, NON-BANNED only (worker task 8) and a session is minted only for a user who
 * is active-in-≥1-team OR pending-in-≥1-non-banned-team (worker task 6); therefore an authenticated
 * session that resolves NO team is, by construction, pending-only. `null` (unauthenticated / still
 * loading) is never pending.
 */
export function isPendingOnlyIdentity(identity: AuthIdentity | null): boolean {
  return identity !== null && identity.teams.length === 0;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Read the current session from the server. A `401` resolves to `null` (no session) rather than
 * throwing — that is the normal unauthenticated answer, not a failure. Any other failure propagates
 * as an {@link ApiError} so the context can surface it. The `/auth/*` routes are top-level (not
 * under `/api/v1`), so this uses the local {@link authFetch} rather than `apiFetch`.
 */
async function fetchSession(signal: AbortSignal): Promise<AuthIdentity | null> {
  try {
    const body = await authFetch<AuthEnvelope<SessionData>>(SESSION_PATH, { signal });
    return parseSessionData(body.data);
  } catch (error) {
    if (error instanceof ApiError && error.kind === "unauthorized") return null;
    throw error;
  }
}

/**
 * A thin same-origin fetch for the `/auth/*` routes, mirroring `apiFetch`'s credential/JSON handling
 * but WITHOUT the `/api/v1` base (auth routes are top-level). Kept local to this module since it is
 * the only consumer; the main `apiFetch` stays the single owner of the `/api/v1` surface.
 */
async function authFetch<T>(
  path: string,
  init: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const headers = new Headers({ accept: "application/json" });
  let requestBody: string | undefined;
  if (init.body !== undefined) {
    headers.set("content-type", "application/json");
    requestBody = JSON.stringify(init.body);
  }
  let response: Response;
  try {
    response = await fetch(path, {
      method: init.method ?? "GET",
      headers,
      credentials: "same-origin",
      ...(requestBody === undefined ? {} : { body: requestBody }),
      ...(init.signal === undefined ? {} : { signal: init.signal }),
    });
  } catch (cause) {
    throw new ApiError("network", "The request could not be sent.", { cause });
  }
  if (response.status === 401) throw new ApiError("unauthorized", "No active session.", { status: 401 });
  if (response.status === 403) throw new ApiError("forbidden", "Not permitted.", { status: 403 });
  if (response.status === 503) throw new ApiError("waking", "The control plane is waking.", { status: 503 });
  if (!response.ok) {
    throw new ApiError(response.status >= 500 ? "server" : "request", `Request failed (${response.status}).`, {
      status: response.status,
    });
  }
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (text.length === 0) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch (cause) {
    throw new ApiError("malformed", "The server returned a response that was not valid JSON.", {
      status: response.status,
      cause,
    });
  }
}

export interface AuthProviderProps {
  readonly children: ReactNode;
  /**
   * A test/bootstrap seam: inject a fully-resolved state and skip the server query. When set, the
   * provider is purely controlled by the caller (used by the DOM tests and any later screen harness).
   */
  readonly value?: AuthState;
}

/**
 * Provides the server-session auth state. In normal operation it runs the session query; a test may
 * pass `value` to drive the state directly (mirroring `IdentityProvider`'s injection seam).
 */
export function AuthProvider(props: AuthProviderProps): ReactNode {
  if (props.value !== undefined) {
    return <AuthContext.Provider value={props.value}>{props.children}</AuthContext.Provider>;
  }
  return <ServerAuthProvider>{props.children}</ServerAuthProvider>;
}

function ServerAuthProvider(props: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();

  const query: UseQueryResult<AuthIdentity | null, ApiError> = useQuery({
    queryKey: AUTH_SESSION_KEY,
    queryFn: ({ signal }) => fetchSession(signal),
    // A 401 is a resolved "no session", not an error, so no special retry is needed; `apiFetch`'s
    // classes drive the client's retry policy (waking/network retried, auth errors not).
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const invalidate = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: AUTH_SESSION_KEY });
  }, [queryClient]);

  const logout = useMutation({
    mutationFn: () => authFetch<void>(LOGOUT_PATH, { method: "POST" }),
    onSettled: () => {
      // Whatever the server said, re-derive from it: a successful logout clears the session; a
      // failure leaves the authoritative state for the next read to reflect.
      queryClient.setQueryData<AuthIdentity | null>(AUTH_SESSION_KEY, null);
      void invalidate();
    },
  });

  const switchTeamMutation = useMutation({
    mutationFn: (tenantId: string) =>
      authFetch<AuthEnvelope<unknown>>(SWITCH_TEAM_PATH, {
        method: "POST",
        body: { tenant_id: tenantId },
      }),
    onSuccess: () => {
      // The server rotated the session and re-resolved (tid, role); re-read so the UI follows the
      // server, never a locally-mutated claim.
      void invalidate();
      // Every /api/* and /auth/* screen is scoped to the SESSION's selected team, so switching teams
      // must invalidate ALL cached queries — otherwise a screen (e.g. the SSO config card, members,
      // access requests, org) keeps showing the PREVIOUS team's data until its own refetch, which can
      // drive a write against the wrong assumed state. No-arg invalidate marks everything stale.
      void queryClient.invalidateQueries();
    },
    onError: () => {
      // The switch was refused (per-team-signin-factors R13.7/R13.8). The server left
      // `selected_tenant` UNCHANGED, so the CURRENT team must stay selected — we deliberately do NOT
      // mutate it client-side. But a refusal can mean the target just became banned (its row no
      // longer resolves) or the operator's own team list shifted under them, so re-read the session
      // to refresh the team list from the server (the sole authority, which already excludes banned
      // teams). The mutation's rejection still propagates to the caller so the switcher can message
      // the refusal; this only reconciles the cached team list.
      void invalidate();
    },
  });

  const signOut = useCallback(async (): Promise<void> => {
    await logout.mutateAsync();
  }, [logout]);

  const switchTeam = useCallback(
    async (tenantId: string): Promise<void> => {
      await switchTeamMutation.mutateAsync(tenantId);
    },
    [switchTeamMutation],
  );

  const refresh = useCallback(async (): Promise<void> => {
    await query.refetch();
  }, [query]);

  const identity = query.data ?? null;
  // A 401 is modelled as `data: null` (no session), so an error present here is a genuine failure.
  const error = query.error ?? null;

  const value = useMemo<AuthState>(
    () => ({
      authenticated: identity !== null,
      pendingApproval: isPendingOnlyIdentity(identity),
      identity,
      loading: query.isPending,
      error,
      refresh,
      signOut,
      signingOut: logout.isPending,
      switchTeam,
      switchingTeam: switchTeamMutation.isPending,
    }),
    [
      identity,
      query.isPending,
      error,
      refresh,
      signOut,
      logout.isPending,
      switchTeam,
      switchTeamMutation.isPending,
    ],
  );

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}

/**
 * Read the auth state when there is a provider, `null` otherwise. For a presentational control that
 * only HIDES an admin-only action (the server enforces it regardless) and must still render in a tree
 * with no session, e.g. a unit-rendered console.
 */
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthContext);
}

/** Read the auth state. Throws if used outside an {@link AuthProvider}. */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (ctx === null) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
